<template>
  <div class="content-page">
    <div>
      <TheBreadcrumb :pages="arianeRoute" />
      <NuxtLazyHydrate when-idle>
        <HeaderContent
          v-if="!source"
          :page="currentPageInfo"
        />
        <HeaderSource
          v-else
          :page="currentPageInfo"
        />
      </NuxtLazyHydrate>
      <div class="post-container">
        <div
          v-if="currentPageInfo.favicon && source"
          class="favicon"
        >
          <img
            :src="currentPageInfo.favicon.url"
            :alt="`favicon de ${currentPageInfo.title}`"
          />
        </div>
        <PostItem
          :source="source"
          :side-content="sideContent"
          :post-content="postContent"
        />
      </div>
      <BlueSection
        :connected="authenticated"
        diagonal="none"
        class="blue-section-item"
        :challenges="monthlyChallenges"
      />
      <DoNotMissSection
        v-if="doNotMiss.length"
        :slider-items="doNotMiss"
      />
      <OfferSection
        v-if="offer"
        :offer="offer"
        >Acheter ce jeu</OfferSection
      >
      <section
        v-if="otherCommunities.length"
        class="other-communities section"
      >
        <div class="title-line line">
          <h2 class="title-strat">ces communautés peuvent vous intéresser</h2>
          <NuxtLink
            to="/communautes"
            class="button-more"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 19.328 34.43"
            >
              <path
                d="M18.71 15.72 3.66.62a2.178 2.178 0 0 0-3.041 0 2.178 2.178 0 0 0 0 3.041l13.555 13.555L.619 30.77a2.178 2.178 0 0 0 0 3.041 2.178 2.178 0 0 0 3.041 0l15.05-15.05a2.178 2.178 0 0 0 0-3.041Z"
                fill="#fff"
                fill-rule="evenodd"
              />
            </svg>
            Plus de communautés
          </NuxtLink>
        </div>
        <div class="slider">
          <CommunitySlider :communities="otherCommunities" />
        </div>
      </section>
      <p class="mention">
        * Récompense provisoire sous réserve de maintenir votre place au
        classement jusqu'à la fin du mois.
      </p>
    </div>
    <PegiWarning v-if="actuality?.community?.pegi === '18'" />
  </div>
</template>

<script setup lang="ts">
import type { Category4 } from '~/types'

definePageMeta({
  middleware: (to) => {
    const loadActualitiesCommuPostTitle = useLoadActusCommuPostTitle()
    return loadActualitiesCommuPostTitle(to)
  },
})

const route = useRoute()

const actualitiesStore = useActualitiesStore()
const challengesStore = useChallengesStore()
const slidersStore = useDoNotMissStore()
const PSActualities = await useFetchPSActualities()

const { monthlyChallenges } = storeToRefs(challengesStore)

const { doNotMiss } = storeToRefs(slidersStore)

const { otherActualities, actuality } = storeToRefs(actualitiesStore)

if (actuality.value?.community?.slug !== route.params.communityName) {
  throw createError({ statusCode: 404 })
}

const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: actuality.value?.community?.title,
      route: `/communautes/${actuality.value?.community?.slug}`,
    },
    {
      title: actuality.value?.category.name,
      route: `/communautes/${actuality.value?.community?.slug}/${actuality.value?.category.slug}`,
    },
    {
      title: actuality.value?.title,
      route: '',
    },
  ]
})

const source = ref(actuality.value?.type === 'source')

const newsArticleSchema = useNewsArticleSchema({
  post: actuality,
  source,
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: actuality.value?.title_seo,
  script: [newsArticleSchema, breadcrumbSchema],
})

useSeoMeta({
  title: actuality.value?.title_seo,
  description: actuality.value?.description_seo,
  keywords: actuality.value?.keywords_seo,
  ogTitle: actuality.value?.title_og,
  ogDescription: actuality.value?.description_og,
  ogImage: actuality.value?.image_og
    ? actuality.value?.image_og.url
    : 'https://www.weareplaystation.fr/img/og-img.jpg',
})

const socialStore = useSocialStore()
const { social } = storeToRefs(socialStore)

const currentPageInfo = computed(() => {
  const rawActuality = unref(actuality)

  const data = {
    title: rawActuality?.title,
    contentType: rawActuality?.type,
    type: rawActuality?.category.name,
    pegi: rawActuality?.community?.pegi,
    image: rawActuality?.image?.url,
    imageLegend: rawActuality?.image_legend,
    follow: social.value.is_following,
    content: undefined,
    subject: undefined,
    favicon: undefined,
  }

  if (rawActuality?.type === 'source') {
    return {
      ...data,
      favicon: rawActuality?.favicon,
      urlVideo: rawActuality?.embed,
    }
  }
  return {
    ...data,
    subject: rawActuality?.community?.title,
    content: rawActuality?.content,
    urlVideo: rawActuality?.video,
  }
})

const popoutStore = usePopoutStore()
const { popout } = popoutStore

const postContent = computed(() => {
  const rawActuality = unref(actuality)
  const rawSocial = unref(social)

  return {
    comments: rawSocial.comments,
    commentsCount: rawSocial.comments_count,
    content: rawActuality?.content,
    contentEmbed: rawActuality?.content_embed,
    contentImages: rawActuality?.content_images,
    createdAt: rawActuality?.created_at,
    description: rawActuality?.description,
    firstTag: {
      title: rawActuality?.community?.title,
      slug: rawActuality?.community?.slug,
    },
    followed: social.value.is_following,
    has_more_comments: rawSocial.has_more_comments,
    likes: rawSocial.likes_count,
    pegi: rawActuality?.community?.pegi,
    shares: rawSocial.shares_count,
    slugType: rawActuality?.category.slug,
    is_liked: rawSocial.is_liked,
    tags: rawActuality?.tags,
    slug: route.params.postTitle as string,
    title: rawActuality?.title,
    type: rawActuality?.category.name,
    url: rawActuality?.link,
    user: rawActuality?.user,
    urlVideo: rawActuality?.video,
    popout,
  }
})

const { offer, contentBlocksType } = useOffer(actuality)

const sideContent = computed(() => {
  const navSide: Category4[] = [{ name: 'La communauté', slug: '' }]

  contentBlocksType.value?.forEach((element) => {
    if (element?.category) {
      const categorySlug = element.category.slug
      if (
        categorySlug === 'actualites-communaute' ||
        categorySlug === 'avis-joueurs' ||
        categorySlug === 'avis-medias' ||
        categorySlug === 'le-coin-des-wapers' ||
        categorySlug === 'astuces'
      ) {
        navSide.push(element.category)
      }
    } else if (element?.slug === 'wapers') {
      navSide.push({ name: 'Les Wapers influents', slug: 'wapers' })
    }
  })

  return {
    title: actuality.value?.community?.title,
    navName: 'actualités',
    type: actuality.value?.category.name,
    route: `/communautes/${actuality.value?.community?.slug}`,
    nav: navSide,
    cards: otherActualities.value,
    subCategoriesFilters: false,
    PSActualities,
  }
})

const otherCommunities = computed(() => {
  const communityBlock = contentBlocksType.value?.find(
    (block) => block?.slug === 'communautes',
  )

  if (!communityBlock?.communities) return []

  return communityBlock.communities.map((item) => ({
    ...item,
    route: `/communautes/${item.slug}`,
  }))
})

const { authenticated } = useAuthenticated()

const loadActualitiesCommuPostTitle = useLoadActusCommuPostTitle()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadActualitiesCommuPostTitle(route)
  }
})
</script>

<style lang="scss" scoped>
.content-page {
  .post-container {
    position: relative;

    .favicon {
      position: absolute;
      top: -4.4rem;
      left: 8rem;
      z-index: 2;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 8.4rem;
      height: 8.4rem;
      background: $white;
      border-radius: 4.2rem;

      img {
        width: 5.6rem;
        height: 3.8rem;
        object-fit: contain;
      }

      @media screen and (max-width: 430px) {
        left: 2.5rem;
        width: 7rem;
        height: 7rem;
        border-radius: 3.5rem;
      }
    }
  }

  .mention {
    margin-top: 10rem;
  }
}

@media screen and (max-width: 700px) {
  .content-page {
    .other-communities {
      padding: 0;
    }

    .mention {
      margin: 2.5em;
    }
  }
}
</style>
